import { Children, useDeferredValue, useEffect, useState } from "react";
import { AddIcon, GlossaryIcon, TagsIcon } from "allIcons";
import {
  AddFromTemplateLibrary,
  BodyContainer,
  FiltersList,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  Loader,
  LoadingListCards,
  MainButton,
  PopOver,
  PrimaryHeading,
  RightMainContainer,
  TemplateNotFound,
} from "commonUI";
import {
  useDefaultGlossary,
  useGlosssary,
  useDeleteDefaultGlossary,
  useAddDefaultGlossary,
  useGetGlossaryCategory,
} from "api/templateLibrary/glossary";
import { useTags } from "api/siteSettings/Tags";
import { Modules, search } from "helpers";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Glossary() {
  const { data: defaultGlossary, isLoading: loadingDefaultGlossary } =
    useDefaultGlossary();
  const { mutate: addDefaultGlossary, isLoading: addingDefaultGlossary } =
    useAddDefaultGlossary();
  const { mutate: deleteDefaultGlossary, isLoading: deletingDefaultGlossary } =
    useDeleteDefaultGlossary();

  const { data: categories } = useGetGlossaryCategory();

  const { data: glossary } = useGlosssary();
  const [addGlossary, setAddGlossary] = useState(false);

  const { data: tags } = useTags();

  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [filteredDefaultGlossary, setFilteredDefaultGlossary] = useState([]);
  const [filterData, setFilterData] = useState({});
  const { remove_items_from_site_template, edit_templates } =
    useUserPermissions();

  useEffect(() => {
    setFilteredDefaultGlossary(
      defaultGlossary.filter((tutorial) =>
        search(tutorial.glossary_title, defferedSearch)
      )
    );
  }, [defaultGlossary, defferedSearch]);

  const applySorting = (order, orderBy) => {
    setFilteredDefaultGlossary((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult, "glossary_title")
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setFilteredDefaultGlossary(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        defaultGlossary,
        "category_id"
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        {deletingDefaultGlossary || addingDefaultGlossary ? (
          <Loader loader="block" />
        ) : (
          ""
        )}
        <HeaderContainer>
          <PrimaryHeading>{t("libraryTemplate.glossary.title")}</PrimaryHeading>
          <div className="relative">
            <MainButton onClick={() => setAddGlossary(true)}>
              <AddIcon />
              {t("button.add")}
            </MainButton>
            <PopOver
              open={addGlossary}
              onClose={() => setAddGlossary(false)}
              style={{ right: "0" }}
              toolTip={{
                right: "35px",
                vertical: "top",
              }}
            >
              <AddFromTemplateLibrary
                data={glossary
                  .filter((_) => String(_.default_tem) !== "1")
                  .map((_) => ({
                    ..._,
                    title: _.glossary_title,
                    icon: <GlossaryIcon fill="white" />,
                    onClick: () => addDefaultGlossary(_.id),
                  }))}
                emptyTemplate={{
                  label: t("libraryTemplate.glossary.title"),
                  link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/glossary/add`,
                  linkLabel: t("libraryTemplate.glossary.title"),
                }}
              />
            </PopOver>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(value) => setSearchText(value)}
            tags={tags}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
            showLibrarySource
            filterAddTitle={t(
              "libraryTemplate.glossary.filter.category.addCategory"
            )}
            filterTitle={t("libraryTemplate.glossary.filter.category.title")}
            filterList={categories?.map((item) => ({
              title: `${item.title}`,
              id: item.id,
              icon: <ListIconWrapper>{<TagsIcon />}</ListIconWrapper>,
              module: Modules.CATEGORY,
              category_id: item.id,
            }))}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {loadingDefaultGlossary ? (
            <LoadingListCards />
          ) : filteredDefaultGlossary.length === 0 ? (
            <TemplateNotFound
              label={t("libraryTemplate.glossary.title")}
              linkLabel={t("libraryTemplate.glossary.title")}
              onClick={() => setAddGlossary(true)}
            />
          ) : (
            Children.toArray(
              filteredDefaultGlossary.map((glossary, index) => {
                return (
                  <ListCard
                    editIcon={edit_templates}
                    link={
                      edit_templates
                        ? `/${ModulePrefixes.SITE_TEMPLATE}/library-item/glossary/${glossary.id}`
                        : ""
                    }
                    strictLink={true}
                    data={glossary.glossary_title}
                    secondaryData={glossary?.category?.title || ""}
                    onCross={() => {
                      deleteDefaultGlossary(glossary.id);
                    }}
                    icon={
                      <ListIconWrapper>
                        <GlossaryIcon fill="white" />
                      </ListIconWrapper>
                    }
                    deleteIcon
                    crossIcon={remove_items_from_site_template}
                    showModalonCross
                    deleteTitle={t(
                      "siteSetting.libraryTemplate.removeTemplate.title"
                    )}
                    deleteButtonText={t(
                      "siteSetting.libraryTemplate.removeTemplate.button"
                    )}
                    deleteMessage={t(
                      "siteSetting.libraryTemplate.removeTemplate.message"
                    )}
                  />
                );
              })
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
